/**
 * Tenant Constants
 *
 * Feature flags, navigation items, and default values for tenant management.
 * Extracted from TenantsPage.tsx for maintainability.
 *
 * @module features/tenants/utils/constants
 */

import type {
  TenantFormData,
  NavItem,
  NavGroup,
  DefaultTabOption,
  FeatureOption,
} from '../types';
import { FEATURE_REGISTRY, DEFAULT_FEATURES } from '../../../config/featureRegistry';

// ============================================================================
// Default Form Values
// ============================================================================

/**
 * Default form data for creating a new tenant.
 */
export const defaultFormData: TenantFormData = {
  slug: '',
  name: '',
  admin_email: '',
  admin_name: '',
  isolation_level: 'row',
  template_slug: '',
  settings: {
    timezone: 'UTC',
    dateFormat: 'YYYY-MM-DD',
    locale: 'en-US',
  },
  branding: {
    primaryColor: '#7c3aed',
    secondaryColor: '#06b6d4',
    logo: '',
    favicon: '',
    appName: '',
  },
  limits: {
    maxUsers: 100,
    maxStorageGB: 50,
    maxApiCallsPerDay: 10000,
  },
  ai_config: {
    enabled: true,
    modelsAllowed: ['claude-3-5-sonnet'],
    dailyTokenLimit: 100000,
    customGuidelines: '',
  },
  features: [...DEFAULT_FEATURES],
  vertical: '',
  navigation_overrides: {
    hiddenItems: [],
    hiddenGroups: [],
    defaultTab: 'dashboard',
  },
};

// ============================================================================
// Navigation Configuration
// ============================================================================

/**
 * Available navigation items that can be hidden per tenant.
 */
export const AVAILABLE_NAV_ITEMS: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', group: null },
  { id: 'users', label: 'Users', group: 'access' },
  { id: 'roles', label: 'Roles', group: 'access' },
  { id: 'requests', label: 'Account Requests', group: 'access' },
  { id: 'sso', label: 'SSO', group: 'access' },
  { id: 'llm', label: 'LLM Models', group: 'ai' },
  { id: 'api', label: 'API Management', group: 'platform' },
  { id: 'schema', label: 'Schema', group: 'platform' },
  { id: 'docs', label: 'Documentation', group: 'platform' },
  { id: 'logs', label: 'Logs', group: 'operations' },
  { id: 'email', label: 'Email', group: 'operations' },
  { id: 'settings', label: 'Settings', group: null },
];

/**
 * Available navigation groups that can be hidden per tenant.
 */
export const AVAILABLE_NAV_GROUPS: NavGroup[] = [
  { id: 'access', label: 'Access Control' },
  { id: 'ai', label: 'AI Services' },
  { id: 'platform', label: 'Platform' },
  { id: 'operations', label: 'Operations' },
];

/**
 * Available default tabs (landing pages) for tenants.
 */
export const AVAILABLE_DEFAULT_TABS: DefaultTabOption[] = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'users', label: 'Users' },
  { id: 'llm', label: 'LLM Models' },
  { id: 'docs', label: 'Documentation' },
];

// ============================================================================
// Feature Flags
// ============================================================================

/**
 * Available feature flags that can be enabled per tenant.
 * Derived from the unified feature registry (WQ-083).
 */
export const AVAILABLE_FEATURES: FeatureOption[] =
  FEATURE_REGISTRY.map(f => ({
    id: f.id,
    label: f.label,
    description: f.description,
  }));

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Normalize hex color input - accepts with or without #.
 * Validates and formats the color string.
 *
 * @param value - The color input string
 * @returns Normalized hex color with # prefix, or empty string if invalid
 */
export const normalizeHexColor = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';

  // Remove any existing # and clean up
  const cleaned = trimmed.replace(/^#+/, '');

  // Validate it looks like a hex color (3 or 6 hex chars)
  if (/^[0-9A-Fa-f]{3}$/.test(cleaned) || /^[0-9A-Fa-f]{6}$/.test(cleaned)) {
    return `#${cleaned}`;
  }

  // If it already has # and is valid, keep it
  if (/^#[0-9A-Fa-f]{3}$/.test(trimmed) || /^#[0-9A-Fa-f]{6}$/.test(trimmed)) {
    return trimmed;
  }

  // For partial input, just add # if missing
  if (cleaned.length > 0) {
    return `#${cleaned}`;
  }

  return trimmed;
};

// ============================================================================
// Filter Options
// ============================================================================

/**
 * Status filter options for tenant list.
 */
export const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'provisioning', label: 'Provisioning' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'archived', label: 'Archived' },
];

/**
 * Isolation level filter options for tenant list.
 */
export const ISOLATION_FILTER_OPTIONS = [
  { value: 'all', label: 'All Isolation Levels' },
  { value: 'row', label: 'Row-Level' },
  { value: 'schema', label: 'Schema-Based' },
  { value: 'container', label: 'Container-Based' },
];

/**
 * Health status filter options for tenant list.
 */
export const HEALTH_FILTER_OPTIONS = [
  { value: 'all', label: 'All Health' },
  { value: 'healthy', label: 'Healthy' },
  { value: 'warning', label: 'Warning' },
  { value: 'critical', label: 'Critical' },
  { value: 'unknown', label: 'Unknown' },
];

// ============================================================================
// Timing Constants
// ============================================================================

/**
 * Polling interval for container status checks (ms).
 */
export const CONTAINER_STATUS_POLL_INTERVAL = 5000;

/**
 * Polling interval for provisioning status checks (ms).
 */
export const PROVISIONING_POLL_INTERVAL = 1000;

/**
 * Timeout for container transitions before showing warning (ms).
 */
export const CONTAINER_TRANSITION_TIMEOUT = 60000;

/**
 * Duration to show success messages (ms).
 */
export const SUCCESS_MESSAGE_DURATION = 3000;
